import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Cart() {
  const { items, remove, setQty, subtotal, clear } = useCart();
  const { user } = useAuth();
  const [coupon, setCoupon] = useState("");
  const [discountPct, setDiscountPct] = useState(0);
  const [checking, setChecking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [settings, setSettings] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    // Show automatic tier discount preview
    if (user?.subscription_tier) {
      api.post("/coupons/validate", { code: "" }).then((r) => setDiscountPct(r.data.total_pct));
    }
  }, [user]);

  const applyCoupon = async () => {
    if (applying) return;
    setApplying(true);
    try {
      const r = await api.post("/coupons/validate", { code: coupon });
      // Only trust this response if it corresponds to the current input value
      if ((r.data.code || "") !== (coupon || "").trim().toUpperCase()) return;
      setDiscountPct(r.data.total_pct);
      if (r.data.coupon_pct > 0) toast.success(`Discount applied: ${r.data.total_pct.toFixed(0)}%`);
      else if (coupon.trim()) toast.error("Invalid code");
    } catch {
      toast.error("Could not validate code");
    } finally {
      setApplying(false);
    }
  };

  const checkout = async () => {
    if (items.length === 0) return;
    if (!user) {
      toast.error("Please log in to check out");
      nav("/login?next=/cart");
      return;
    }
    setChecking(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        coupon_code: coupon || undefined,
      };
      const r = await api.post("/payments/checkout", payload);
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setChecking(false);
    }
  };

  const discount = subtotal * (discountPct / 100);
  const total = Math.max(0, subtotal - discount);

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-12">
      <h1 className="font-display text-5xl">Cart</h1>

      {items.length === 0 ? (
        <div className="mt-12 text-center py-24 border border-dashed border-white/10 rounded">
          <p className="text-zinc-400">{settings.cart_empty_message || "Your cart is empty."}</p>
          <Link to="/shop" className="btn-primary inline-block mt-6" data-testid="empty-cart-shop">Shop Cards</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-10 mt-10">
          <div className="md:col-span-2 space-y-3" data-testid="cart-items">
            {items.map((it) => (
              <div key={it.id} className="flex gap-4 border border-white/10 rounded p-4 bg-[var(--cl-surface)]">
                <img src={it.image} alt={it.title} className="w-24 h-32 object-cover rounded" />
                <div className="flex-1">
                  <div className="font-medium">{it.title}</div>
                  <div className="text-zinc-500 text-sm mt-1">${it.price.toLocaleString()}</div>
                  <div className="flex items-center gap-3 mt-3">
                    <button onClick={() => setQty(it.id, it.quantity - 1)} className="p-2 border border-white/10 rounded" data-testid={`qty-dec-${it.id}`}><Minus size={14} /></button>
                    <span data-testid={`qty-${it.id}`}>{it.quantity}</span>
                    <button onClick={() => setQty(it.id, it.quantity + 1)} className="p-2 border border-white/10 rounded" data-testid={`qty-inc-${it.id}`}><Plus size={14} /></button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl">${(it.price * it.quantity).toLocaleString()}</div>
                  <button onClick={() => remove(it.id)} className="text-zinc-500 hover:text-[var(--cl-primary)] mt-3" data-testid={`remove-${it.id}`}><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
            <button onClick={clear} className="text-xs text-zinc-500 hover:text-white mt-4" data-testid="clear-cart">Clear cart</button>
          </div>

          <aside className="border border-white/10 rounded p-6 bg-[var(--cl-surface)] h-fit">
            <div className="text-label text-zinc-500">Summary</div>
            <div className="mt-4 space-y-3 text-sm">
              <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
              {discount > 0 && <Row label={`Discount (${discountPct.toFixed(0)}%)`} value={`-$${discount.toFixed(2)}`} highlight />}
              <div className="border-t border-white/10 pt-3 flex justify-between">
                <span className="text-label text-zinc-400">Total</span>
                <span className="font-display text-3xl" data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
            </div>
            <div className="mt-6">
              <label className="text-label text-zinc-500">Coupon Code</label>
              <div className="flex gap-2 mt-2">
                <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="LOBSTER10"
                       data-testid="coupon-input"
                       className="flex-1 bg-black border border-white/10 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--cl-primary)]" />
                <button onClick={applyCoupon} disabled={applying} className="btn-outline text-xs px-4" data-testid="apply-coupon">
                  {applying ? "…" : "Apply"}
                </button>
              </div>
              <div className="text-xs text-zinc-500 mt-2">{settings.cart_coupon_hint || "Try: LOBSTER10, BREAKS20"}</div>
            </div>
            <button disabled={checking} onClick={checkout} className="btn-primary w-full mt-6" data-testid="checkout-btn">
              {checking ? "Redirecting…" : (settings.cart_checkout_button || "Checkout with Stripe")}
            </button>
            {!user && <div className="mt-3 text-xs text-zinc-500 text-center">
              <Link to="/login?next=/cart" className="text-[var(--cl-orange)] hover:underline">Log in</Link> for tier discounts
            </div>}
          </aside>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex justify-between">
      <span className="text-zinc-400">{label}</span>
      <span className={highlight ? "text-[var(--cl-orange)]" : ""}>{value}</span>
    </div>
  );
}
